// Count-ups and countdowns: how long ago something happened, and how long until its next
// anniversary ("HRT birthday in 23 days"). Works at whatever precision the date has: a full
// date gives days, a month gives months, a year alone gives years.

import { parseGedcomDate, type SimpleDate } from './dates';
import type { EventType, Id, LifeEvent, Person, Tree } from './types';
import { birthDate, deathDate, isDeceased } from './people';

export interface CalendarDate {
  year: number;
  month?: number; // 1-12
  day?: number;
}

/** A plain Gregorian date we can count from: exact or "about", not a range or phrase. */
export function countableDate(raw: string | undefined): CalendarDate | undefined {
  const p = parseGedcomDate(raw);
  if (!p?.start || p.end) return undefined;
  if (p.qualifier !== 'exact' && p.qualifier !== 'about' && p.qualifier !== 'calculated' && p.qualifier !== 'estimated') return undefined;
  const s: SimpleDate = p.start;
  if (s.bce || (s.calendar && !/GREGORIAN/.test(s.calendar))) return undefined;
  return { year: s.year, month: s.month, day: s.day };
}

const DAY = 86_400_000;
const utc = (y: number, m: number, d: number) => Date.UTC(y, m - 1, d);

function todayParts(now: Date): { year: number; month: number; day: number } {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/** Feb 29 anniversaries fall on Feb 28 in other years. */
function anniversaryDay(year: number, month: number, day: number): number {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Math.min(day, last);
}

export interface Elapsed {
  years: number;
  months: number;
  days: number;
  /** True when the date is after today. */
  future: boolean;
  precision: 'day' | 'month' | 'year';
}

/** Whole years, months, and days between `d` and today (either direction). */
export function elapsed(d: CalendarDate, now = new Date()): Elapsed {
  const t = todayParts(now);
  const precision = d.day ? 'day' : d.month ? 'month' : 'year';
  const from = { year: d.year, month: d.month ?? 1, day: d.day ?? 1 };
  const future = utc(from.year, from.month, from.day) > utc(t.year, t.month, t.day);
  const [a, b] = future ? [t, from] : [from, t];
  // Whole months are counted to each monthly anniversary, which falls on the month's last day
  // when the month is shorter (Jan 31 → Feb 28), so a Feb 29 birthday comes round on Feb 28.
  const after = (n: number) => {
    const m = a.month - 1 + n;
    const year = a.year + Math.floor(m / 12);
    const month = (m % 12) + 1;
    return utc(year, month, anniversaryDay(year, month, a.day));
  };
  const end = utc(b.year, b.month, b.day);
  let total = (b.year - a.year) * 12 + (b.month - a.month);
  if (after(total) > end) total--;
  const days = Math.round((end - after(total)) / DAY);
  return { years: Math.floor(total / 12), months: total % 12, days, future, precision };
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "3 years, 2 months", "5 days", "less than a month". */
export function durationText(e: Elapsed): string {
  if (e.precision === 'year') return e.years ? plural(e.years, 'year') : 'less than a year';
  const parts: string[] = [];
  if (e.years) parts.push(plural(e.years, 'year'));
  if (e.months) parts.push(plural(e.months, 'month'));
  if (e.precision === 'day' && e.days && e.years < 2) parts.push(plural(e.days, 'day'));
  if (!parts.length) return e.precision === 'day' ? 'today' : 'less than a month';
  return parts.slice(0, 2).join(', ');
}

/** "3 years, 2 months ago" or "in 5 days". */
export function relativeText(raw: string | undefined, now = new Date()): string | undefined {
  const d = countableDate(raw);
  if (!d) return undefined;
  const e = elapsed(d, now);
  const text = durationText(e);
  if (text === 'today') return 'today';
  const approx = parseGedcomDate(raw)?.qualifier !== 'exact' ? 'about ' : '';
  return e.future ? `in ${approx}${text}` : `${approx}${text} ago`;
}

export interface Anniversary {
  /** Days until the next one; 0 = today. */
  days: number;
  /** Which anniversary it will be (1st, 2nd…). */
  count: number;
  date: Date;
}

/** The next anniversary of a full date (none for dates without a day, or in the future). */
export function nextAnniversary(raw: string | undefined, now = new Date()): Anniversary | undefined {
  const d = countableDate(raw);
  if (!d?.month || !d.day) return undefined;
  if (parseGedcomDate(raw)?.qualifier !== 'exact') return undefined;
  const t = todayParts(now);
  const today = utc(t.year, t.month, t.day);
  let year = t.year;
  let when = utc(year, d.month, anniversaryDay(year, d.month, d.day));
  if (when < today) {
    year++;
    when = utc(year, d.month, anniversaryDay(year, d.month, d.day));
  }
  const count = year - d.year;
  if (count < 1) return undefined;
  return { days: Math.round((when - today) / DAY), count, date: new Date(when) };
}

export function ordinalWord(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function inDays(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/** What an event's anniversary is called, for the events that have one worth counting down to. */
const ANNIVERSARY_NAMES: Partial<Record<EventType, string>> = {
  'hrt-start': 'HRT birthday',
  'gender-transition': 'Transition anniversary',
  'coming-out': 'Coming-out anniversary',
  'name-change': 'Name day',
  adoption: 'Adoption anniversary',
  death: 'Death anniversary',
  graduation: 'Graduation anniversary',
  immigration: 'Arrival anniversary',
  naturalization: 'Citizenship anniversary',
  custom: 'Anniversary',
};

export function anniversaryName(type: EventType): string | undefined {
  return ANNIVERSARY_NAMES[type];
}

/** The specific kind of date being counted down to, for showing or hiding each kind. */
export type CountdownType = 'birthday' | 'partnership' | EventType;

/** Plural names for each kind of countdown, in the order they're offered. */
export const COUNTDOWN_TYPE_LABELS: Partial<Record<CountdownType, string>> = {
  birthday: 'Birthdays',
  partnership: 'Partner anniversaries',
  'hrt-start': 'HRT birthdays',
  'gender-transition': 'Transition anniversaries',
  'coming-out': 'Coming-out anniversaries',
  'name-change': 'Name days',
  adoption: 'Adoption anniversaries',
  graduation: 'Graduation anniversaries',
  immigration: 'Arrival anniversaries',
  naturalization: 'Citizenship anniversaries',
  custom: 'Other anniversaries',
  death: 'Death anniversaries',
};

/** "HRT birthday in 23 days (3rd)" for events that have anniversaries. */
export function eventCountdown(e: LifeEvent, now = new Date()): string | undefined {
  const name = anniversaryName(e.type);
  if (!name) return undefined;
  const a = nextAnniversary(e.date, now);
  if (!a) return undefined;
  return `${name} ${inDays(a.days)} (${ordinalWord(a.count)})`;
}

export interface Age {
  /** The youngest they could be. */
  age: number;
  /** The oldest they could be, when the dates don't settle it (a birth year alone). */
  upTo?: number;
  atDeath: boolean;
}

/** The first and last days a partial date could mean: 1958 is Jan 1 to Dec 31, 1958. */
const firstDay = (d: CalendarDate) => ({ year: d.year, month: d.month ?? 1, day: d.day ?? 1 });
function lastDay(d: CalendarDate) {
  const month = d.month ?? 12;
  return { year: d.year, month, day: d.day ?? new Date(Date.UTC(d.year, month, 0)).getUTCDate() };
}
const localDate = (d: { year: number; month: number; day: number }) => new Date(d.year, d.month - 1, d.day);

/**
 * Whole years from one date to another (a Date is that day, such as today): someone's age at an
 * event. Undefined when either date can't be counted from, or the end is before the start.
 */
export function yearsBetween(from: string | undefined, to: string | Date | undefined): Omit<Age, 'atDeath'> | undefined {
  const b = countableDate(from);
  if (!b || to === undefined) return undefined;
  const d = to instanceof Date ? undefined : countableDate(to);
  if (!(to instanceof Date) && !d) return undefined;
  // Partial dates leave a range: youngest from the latest possible start to the earliest possible end.
  const end = d ? { early: localDate(firstDay(d)), late: localDate(lastDay(d)) } : { early: to as Date, late: to as Date };
  const oldest = elapsed(firstDay(b), end.late);
  if (oldest.future) return undefined;
  const youngest = elapsed(lastDay(b), end.early);
  const age = youngest.future ? 0 : youngest.years;
  return { age, ...(oldest.years !== age ? { upTo: oldest.years } : {}) };
}

/** Age today, or at death. Undefined without a usable birth date. */
export function ageOf(p: Person, now = new Date()): Age | undefined {
  const death = countableDate(deathDate(p)) ? deathDate(p) : undefined;
  if (!death && isDeceased(p)) return undefined;
  const span = yearsBetween(birthDate(p), death ?? now);
  return span && { ...span, atDeath: !!death };
}

/** The middle of an age range, for averaging: 67–68 counts as 67.5. */
export const midAge = (a: { age: number; upTo?: number }) => (a.age + (a.upTo ?? a.age)) / 2;

/** "34", or "67–68" when the dates allow either. */
export function ageText(a: Omit<Age, 'atDeath'>): string {
  return a.upTo !== undefined ? `${a.age}–${a.upTo}` : String(a.age);
}

/** One line for the details panel: "34 years old · birthday in 23 days" or "Died 8 years ago". */
export function personCountdown(p: Person, now = new Date()): string | undefined {
  const parts: string[] = [];
  const age = ageOf(p, now);
  if (!isDeceased(p)) {
    if (age) parts.push(`${ageText(age)} years old`);
    const bday = nextAnniversary(birthDate(p), now);
    if (bday) parts.push(bday.days === 0 ? `turns ${bday.count} today` : `turns ${bday.count} ${inDays(bday.days)}`);
  } else {
    const since = relativeText(deathDate(p), now);
    if (since) parts.push(`Died ${since}`);
    if (age?.atDeath) parts.push(`at ${ageText(age)}`);
    const b = nextAnniversary(birthDate(p), now);
    if (b && b.days <= 60) parts.push(`would have turned ${b.count} ${inDays(b.days)}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
}

/** Birthdays of people not known to have died; anniversaries of events and partnerships; remembrance of deaths. */
export type UpcomingKind = 'birthday' | 'anniversary' | 'remembrance';

export interface Upcoming {
  personId: Id;
  /** The partner, for a partnership anniversary. */
  otherId?: Id;
  label: string;
  kind: UpcomingKind;
  type: CountdownType;
  days: number;
  count: number;
  date: Date;
}

/** Birthdays, HRT birthdays, and other anniversaries coming up in the next `withinDays`. */
export function upcomingAnniversaries(people: Person[], withinDays = 60, now = new Date()): Upcoming[] {
  const out: Upcoming[] = [];
  for (const p of people) {
    if (!isDeceased(p)) {
      const b = nextAnniversary(birthDate(p), now);
      if (b && b.days <= withinDays) out.push({ personId: p.id, label: `turns ${b.count}`, kind: 'birthday', type: 'birthday', ...b });
    }
    for (const e of p.events) {
      const name = anniversaryName(e.type);
      if (!name || (e.type !== 'death' && isDeceased(p))) continue;
      const a = nextAnniversary(e.date, now);
      if (a && a.days <= withinDays) out.push({ personId: p.id, label: `${name} (${ordinalWord(a.count)})`, kind: e.type === 'death' ? 'remembrance' : 'anniversary', type: e.type, ...a });
    }
  }
  return out.sort((a, b) => a.days - b.days);
}

/** Everything coming up in a tree: people's dates plus anniversaries of current partnerships. Soonest first. */
export function treeCountdowns(tree: Tree, withinDays = 365, now = new Date()): Upcoming[] {
  const out = upcomingAnniversaries(Object.values(tree.people), withinDays, now);
  for (const r of Object.values(tree.relationships)) {
    if (r.kind !== 'partner' || r.status !== 'current' || !tree.people[r.a] || !tree.people[r.b]) continue;
    if (isDeceased(tree.people[r.a]) || isDeceased(tree.people[r.b])) continue;
    const a = nextAnniversary(r.start, now);
    if (!a || a.days > withinDays) continue;
    out.push({ personId: r.a, otherId: r.b, label: `${ordinalWord(a.count)} ${r.type === 'married' ? 'wedding anniversary' : 'anniversary'}`, kind: 'anniversary', type: 'partnership', ...a });
  }
  return out.sort((a, b) => a.days - b.days);
}
